import {
   getDateFromDateObject,
   getDateObjectFromDateString,
   sanitizeFileName
} from '@flowwai-frontend/shared/utils/common'
import { SERVER_DATE_TIME_FORMAT } from '@flowwai-frontend/shared/constants'

import {
   UnionFieldResponse,
   UnionFieldResponseType,
   UnionFieldType,
   UnionFieldResponseModel
} from '../types/Field'
import AttachmentModel from '../models/AttachmentModel'
import { RelatedRecord } from '../types/Field/relationField'
import {
   FieldVerificationType,
   PhoneNumberResponse
} from '../types/Field/field'
import { GeoShapeFieldResponseType } from '../types/Field/geoLocationField'
import { validatePhoneNumberField } from './fieldValidations'

export const isFieldResponseAvailable = (
   fieldResponse: UnionFieldResponse
): boolean => {
   switch (fieldResponse.fieldType) {
      case 'CHECKBOX_GROUP': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'DATE_TIME':
      case 'TIME':
      case 'DATE': {
         return fieldResponse.response ? true : false
      }

      case 'DROPDOWN': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'EMAIL': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'FIELDS_SELECTOR': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'FILE_UPLOADER': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'FLOAT': {
         const response = fieldResponse.response
         return response && response.toString().trim() ? true : false
      }

      case 'GOF_SELECTOR': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'IMAGE_UPLOADER': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'INTEGER': {
         const response = fieldResponse.response
         return response && response.toString().trim() ? true : false
      }

      case 'LONG_TEXT': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'MULTI_SEARCHABLE': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'MULTI_SELECT': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'PHONE_NUMBER': {
         const response = fieldResponse.response
         return response?.dialCode &&
            response?.iso2CountryCode &&
            response?.phoneNumber
            ? true
            : false
      }

      case 'PLAIN_TEXT': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'SHORT_TEXT': {
         const response = fieldResponse.response
         return response?.value.trim() ? true : false
      }

      case 'RADIO_GROUP': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'SEARCHABLE': {
         const response = fieldResponse.response
         return response ? true : false
      }

      case 'URL': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'RELATION': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'GEO_LOCATION': {
         const response = fieldResponse.response
         return response ? true : false
      }
      case 'GEO_SHAPE': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      default:
         return false
   }
}

//FIXME: resolve type issues
export const fieldResponseRequestObj = (
   fieldType: UnionFieldType,
   response: UnionFieldResponseType
): string | null => {
   switch (fieldType) {
      case 'CHECKBOX_GROUP':
         return response ? JSON.stringify(response) : null
      case 'DATE': {
         return response ? getDateFromDateObject(response as Date) : null
      }
      case 'DATE_TIME': {
         return response
            ? getDateFromDateObject(response as Date, SERVER_DATE_TIME_FORMAT)
            : null
      }
      case 'DROPDOWN':
         return response ? (response as string) : null
      case 'EMAIL':
         return response ? (response as string) : null
      case 'FIELDS_SELECTOR':
         return response ? (response as string) : null
      case 'FILE_UPLOADER': {
         if (response) {
            const attachmentsData = (response as AttachmentModel[]).map(
               (attachment: AttachmentModel) => ({
                  file_id:
                     attachment.attachmentId === '-1'
                        ? undefined
                        : attachment.attachmentId,
                  name: sanitizeFileName(attachment.name),
                  mime_type: attachment.mimeType,
                  size_in_bytes: attachment.sizeInBytes,
                  url: attachment.url
               })
            )
            return JSON.stringify(attachmentsData)
         }

         return null
      }
      case 'RELATION': {
         if (response) {
            const recordIds = (response as RelatedRecord[]).map(
               record => record.pipelineItemId
            )
            return JSON.stringify(recordIds)
         }
         return null
      }
      case 'FLOAT':
         return response ? (response as string) : null
      case 'GOF_SELECTOR':
         return response ? (response as string) : null
      case 'IMAGE_UPLOADER':
         return response ? (response as string) : null
      case 'INTEGER':
         return response ? (response as string) : null
      case 'LONG_TEXT':
         return response ? (response as string) : null
      case 'MULTI_SEARCHABLE':
         //TODO: need to check and update
         return response ? JSON.stringify(response) : null
      case 'MULTI_SELECT':
         return response ? JSON.stringify(response) : null
      case 'PHONE_NUMBER': {
         const phoneNumberResponse = response as PhoneNumberResponse

         const isPhoneNumberNotAvailable =
            phoneNumberResponse.phoneNumber.trim() === ''

         if (isPhoneNumberNotAvailable) return null

         const requestObj = {
            phone_number: phoneNumberResponse.phoneNumber,
            dial_code: phoneNumberResponse.dialCode,
            iso2_country_code: phoneNumberResponse.iso2CountryCode
         }
         return JSON.stringify(requestObj)
      }
      case 'PLAIN_TEXT':
         return response ? (response as string) : null
      case 'SHORT_TEXT':
         return response ? JSON.stringify(response) : null
      case 'RADIO_GROUP':
         return response ? (response as string) : null
      case 'SEARCHABLE':
         //TODO: need to check and update
         return response ? (response as string) : null
      case 'URL':
         return response ? (response as string) : null
      case 'TIME':
         return response ? (response as string) : null //TODO: need to update when we add time field
      case 'GEO_LOCATION':
         return response ? JSON.stringify(response) : null
      case 'GEO_SHAPE': {
         const geoShapeResponse = response as GeoShapeFieldResponseType
         if (!geoShapeResponse || !geoShapeResponse.length) return null

         return JSON.stringify(geoShapeResponse)
      }

      default:
         return null
   }
}

// Inverse of fieldResponseRequestObj — deserializes the PMU recompute API's
// `response: String` wire format back into the in-memory UnionFieldResponseType.
// The server serializes using the same dialect, so every case mirrors its twin
// above exactly in reverse.
export const parseWireFieldResponse = (
   fieldType: UnionFieldType,
   wireResponse: string | null | undefined
): UnionFieldResponseType => {
   if (wireResponse == null || wireResponse === '') return null

   switch (fieldType) {
      // Pass-through scalar types — server returns the value as-is.
      case 'PLAIN_TEXT':
      case 'LONG_TEXT':
      case 'EMAIL':
      case 'URL':
      case 'FLOAT':
      case 'INTEGER':
      case 'DROPDOWN':
      case 'FIELDS_SELECTOR':
      case 'GOF_SELECTOR':
      case 'IMAGE_UPLOADER':
      case 'RADIO_GROUP':
      case 'SEARCHABLE':
      case 'TIME':
         return wireResponse

      case 'DATE': {
         // Serializer: getDateFromDateObject(date) — default SERVER_DATE_FORMAT
         const parsed = getDateObjectFromDateString(wireResponse)
         return isNaN(parsed.getTime()) ? null : parsed
      }

      case 'DATE_TIME': {
         // Serializer: getDateFromDateObject(date, SERVER_DATE_TIME_FORMAT)
         const parsed = getDateObjectFromDateString(wireResponse)
         return isNaN(parsed.getTime()) ? null : parsed
      }

      case 'CHECKBOX_GROUP':
      case 'MULTI_SEARCHABLE':
      case 'MULTI_SELECT': {
         // Serializer: JSON.stringify(response) — response is string[]
         try {
            return JSON.parse(wireResponse)
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               `parseWireFieldResponse: malformed JSON for ${fieldType}`,
               wireResponse
            )
            return null
         }
      }

      case 'SHORT_TEXT': {
         // Serializer: JSON.stringify(response) — response is ShortTextValue
         try {
            return JSON.parse(wireResponse)
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               'parseWireFieldResponse: malformed JSON for SHORT_TEXT',
               wireResponse
            )
            return null
         }
      }

      case 'PHONE_NUMBER': {
         // Serializer: JSON.stringify({ phone_number, dial_code, iso2_country_code })
         // snake_case on the wire — convert to camelCase for PhoneNumberResponse.
         try {
            const raw = JSON.parse(wireResponse) as {
               phone_number: string
               dial_code: string
               iso2_country_code: string
            }
            const result: PhoneNumberResponse = {
               phoneNumber: raw.phone_number,
               dialCode: raw.dial_code,
               iso2CountryCode: raw.iso2_country_code
            }
            return result
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               'parseWireFieldResponse: malformed JSON for PHONE_NUMBER',
               wireResponse
            )
            return null
         }
      }

      case 'FILE_UPLOADER': {
         // Serializer: JSON.stringify([{ file_id, name, mime_type, size_in_bytes, url }, ...])
         // Reconstruct AttachmentModel[] to match the shape from getFieldResponsesBasedOnFieldType.
         try {
            const raw = JSON.parse(wireResponse) as Array<{
               file_id?: string
               name?: string
               mime_type?: string
               size_in_bytes?: number
               url?: string
            }>
            return raw.map(item =>
               AttachmentModel.createAttachmentModel({
                  attachmentId: item.file_id ?? '-1',
                  name: item.name ?? null,
                  mimeType: item.mime_type ?? null,
                  sizeInBytes: item.size_in_bytes ?? null,
                  url: item.url ?? '',
                  preSignedUrl: item.url ?? ''
               })
            )
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               'parseWireFieldResponse: malformed JSON for FILE_UPLOADER',
               wireResponse
            )
            return null
         }
      }

      case 'RELATION': {
         // Serializer: JSON.stringify(recordIds: string[]) — strips all fields
         // except pipelineItemId, so full RelatedRecord shapes are not round-
         // trippable from wire alone. We produce skeletal records to avoid
         // clobbering in-memory records that carry richer data (name, type, etc.).
         // Callers that need full detail should prefer the in-memory value when
         // it is already populated.
         try {
            const ids = JSON.parse(wireResponse) as string[]
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return ids.map(id => ({ pipelineItemId: id } as RelatedRecord))
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               'parseWireFieldResponse: malformed JSON for RELATION',
               wireResponse
            )
            return null
         }
      }

      case 'GEO_LOCATION': {
         // Serializer: JSON.stringify(response) — response is GeoLocationDetails
         try {
            return JSON.parse(wireResponse)
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               'parseWireFieldResponse: malformed JSON for GEO_LOCATION',
               wireResponse
            )
            return null
         }
      }

      case 'GEO_SHAPE': {
         // Serializer: JSON.stringify(geoShapeResponse) — response is GeoLocationDetails[]
         try {
            return JSON.parse(wireResponse)
         } catch {
            // eslint-disable-next-line no-console
            console.warn(
               'parseWireFieldResponse: malformed JSON for GEO_SHAPE',
               wireResponse
            )
            return null
         }
      }

      default:
         return null
   }
}

export const getIsFieldResponseUpdated = (
   originalFieldResponse: UnionFieldResponse,
   updatedFieldResponse: UnionFieldResponse
): boolean => {
   switch (originalFieldResponse.fieldType) {
      case 'CHECKBOX_GROUP':
      case 'MULTI_SEARCHABLE':
      case 'MULTI_SELECT': {
         const stringifiedOriginalFieldResponse = originalFieldResponse.response
            ?.length
            ? JSON.stringify(originalFieldResponse.response?.slice().sort())
            : ''

         const stringifiedUpdatedFieldResponse = (updatedFieldResponse.response as Array<
            any
         >)?.length
            ? JSON.stringify(
                 (updatedFieldResponse.response as Array<any>)?.slice().sort()
              )
            : ''

         return (
            stringifiedOriginalFieldResponse !== stringifiedUpdatedFieldResponse
         )
      }

      default: {
         const stringifiedOriginalFieldResponse = JSON.stringify(
            originalFieldResponse.response
         )
         const stringifiedUpdatedFieldResponse = JSON.stringify(
            updatedFieldResponse.response
         )

         return (
            stringifiedOriginalFieldResponse !== stringifiedUpdatedFieldResponse
         )
      }
   }
}

export const getUnionFieldResponseTypeFromUnionFieldResponseModel = (
   fieldResponseModel: UnionFieldResponseModel,
   isDirtyResponse = false
): UnionFieldResponse => ({
   fieldResponseId: fieldResponseModel.fieldResponseId,
   fieldId: fieldResponseModel.field.fieldId,
   fieldType: fieldResponseModel.fieldType,
   response: isDirtyResponse
      ? (fieldResponseModel.dirtyResponse as any)
      : (fieldResponseModel?.response as any)
})

export const getIsFieldResponseAvailableFromFieldId = (
   fieldId: string,
   getFieldResponseModel: (fieldId: string) => UnionFieldResponseModel
): boolean => {
   try {
      const fieldResponseModel = getFieldResponseModel(fieldId)

      return isFieldResponseAvailable(
         getUnionFieldResponseTypeFromUnionFieldResponseModel(
            fieldResponseModel
         )
      )
   } catch {
      return false
   }
}

export const getFieldVerificationStatusFromResponse = (
   response: UnionFieldResponseType,
   fieldType: UnionFieldType
): FieldVerificationType | null => {
   switch (fieldType) {
      case 'PHONE_NUMBER': {
         const phoneNumberResponse = response as PhoneNumberResponse
         return phoneNumberResponse?.otpVerificationStatus ?? null
      }
      default: {
         return null
      }
   }
}

export const getIsValidFieldResponse = (
   response: UnionFieldResponse,
   fieldType: UnionFieldType
): boolean => {
   const hasFieldResponse = isFieldResponseAvailable(response)

   if (!hasFieldResponse) return false

   switch (fieldType) {
      case 'PHONE_NUMBER': {
         return validatePhoneNumberField(
            response.response as PhoneNumberResponse
         ).isValid
      }
      default: {
         return false
      }
   }
}
